import { Box, Button, Flex, Input, Spinner, Switch, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Save, Send } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useUser } from "../../hooks/use-user";
import useApiRequest from "../../lib/hooks/useRequest";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../ui/components/ui/form";

interface MetaTrackingSettingsResponse {
  configured: boolean;
  pixelId?: string | null;
  enabled: boolean;
  accessTokenMasked?: string | null;
  testEventCode?: string | null;
}

interface MetaTestEventResponse {
  success: boolean;
  errorMessage?: string | null;
}

const formSchema = z.object({
  pixelId: z.string().regex(/^\d{10,20}$/, "Die Pixel-ID besteht nur aus Ziffern und ist 10 bis 20 Zeichen lang"),
  accessToken: z.union([
    z.string().min(20, "Ein Meta Access Token ist deutlich länger – bitte den vollständigen Wert einfügen"),
    z.literal(""),
  ]),
  enabled: z.boolean(),
  testEventCode: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

const ConversionTracking = () => {
  const { apiRequest } = useApiRequest();
  const { user } = useUser();
  const [settings, setSettings] = useState<MetaTrackingSettingsResponse>();
  const [loadError, setLoadError] = useState<string>();
  const [testResult, setTestResult] = useState<MetaTestEventResponse>();
  const [isSendingTest, setSendingTest] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { pixelId: "", accessToken: "", enabled: false, testEventCode: "" },
  });

  const loadSettings = useCallback(async () => {
    setLoadError(undefined);
    const response = await apiRequest<MetaTrackingSettingsResponse>("meta/tracking-settings", "GET");

    if (response?.data) {
      setSettings(response.data);
      form.reset({
        pixelId: response.data.pixelId ?? "",
        accessToken: "",
        enabled: response.data.enabled,
        testEventCode: response.data.testEventCode ?? "",
      });
      return;
    }

    // Without this the component would keep showing a spinner forever - the endpoint is
    // superadmin-only, so a 403 is a realistic outcome.
    setLoadError(
      response?.status === 403
        ? "Für diesen Bereich fehlen dir die Rechte."
        : "Die Einstellungen konnten nicht geladen werden."
    );
    // form is deliberately not in the dependencies: reset would otherwise run on every keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRequest]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const onSubmit = async (values: FormValues) => {
    const response = await apiRequest<MetaTrackingSettingsResponse>("meta/tracking-settings", "PUT", {
      body: {
        pixelId: values.pixelId,
        enabled: values.enabled,
        ...(values.accessToken ? { accessToken: values.accessToken } : {}),
        ...(values.testEventCode ? { testEventCode: values.testEventCode } : {}),
        // Which customer this form belongs to, as this page learned it when it was
        // loaded. A superadmin acts as whichever organization they are signed in as at
        // the moment of the request, and that can change in another tab without this
        // page noticing. The server compares the two and refuses rather than writing
        // one customer's pixel onto another.
        ...(user?.organizationId ? { expectedOrganizationId: user.organizationId } : {}),
      },
      toast: { toastText: "Conversion-Tracking gespeichert" },
    });

    if (response?.data) {
      setSettings(response.data);
      setTestResult(undefined);
      form.setValue("accessToken", "");
    }
  };

  const sendTestEvent = async () => {
    setSendingTest(true);
    setTestResult(undefined);

    const response = await apiRequest<MetaTestEventResponse>("meta/tracking-settings/test-event", "POST");

    setTestResult(response?.data);
    setSendingTest(false);
  };

  if (loadError) {
    return (
      <Flex className="flex-col gap-3">
        <Text className="text-red-600 text-sm">{loadError}</Text>
        <Button type="button" variant="outline" colorScheme="primary" className="w-fit" onClick={loadSettings}>
          Erneut versuchen
        </Button>
      </Flex>
    );
  }

  if (!settings) {
    return <Spinner />;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full flex flex-col gap-8 h-full bg-white">
        <Flex className="h-full flex-col">
          <Text className="text-gray-400 text-sm mb-6">
            Meldet abgeschickte Bewerbungen als Conversion an das Meta-Dataset dieser Organisation. Pixel-ID und Access
            Token stehen im Meta Events Manager unter „Datenquellen“. Das Tracking ist standardmäßig aus – erst
            einschalten, wenn der Perspective-Funnel dieses Kunden deaktiviert ist, sonst zählt Meta doppelt.
          </Text>

          <FormField
            control={form.control}
            name="pixelId"
            render={({ field }) => (
              <FormItem className="flex gap-4 mb-4 w-full grow">
                <FormLabel className="max-w-52 w-full font-bold mt-4 text-md">
                  Pixel-ID <span className="text-primary">*</span>
                </FormLabel>
                <Box className="w-full">
                  <FormControl>
                    <Input placeholder="15-stellige Dataset-ID aus dem Events Manager" {...field} />
                  </FormControl>
                  <FormMessage />
                </Box>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="accessToken"
            render={({ field }) => (
              <FormItem className="flex gap-4 mb-4 w-full grow">
                <FormLabel className="max-w-52 w-full font-bold mt-4 text-md">Access Token</FormLabel>
                <Box className="w-full">
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="off"
                      placeholder={
                        settings.configured
                          ? `Gespeichert (${settings.accessTokenMasked}) – nur ausfüllen, um es zu ersetzen`
                          : "Token aus dem Events Manager einfügen"
                      }
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  <Text className="text-gray-400 text-xs mt-1">
                    Wird verschlüsselt gespeichert und nie wieder ausgegeben. Leer lassen heißt: unverändert.
                  </Text>
                </Box>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="testEventCode"
            render={({ field }) => (
              <FormItem className="flex gap-4 mb-4 w-full grow">
                <FormLabel className="max-w-52 w-full font-bold mt-4 text-md">Test-Event-Code</FormLabel>
                <Box className="w-full">
                  <FormControl>
                    <Input placeholder="optional, z. B. TEST12345" {...field} />
                  </FormControl>
                  <FormMessage />
                  <Text className="text-gray-400 text-xs mt-1">
                    Aus dem Events Manager unter „Test events“. Damit erscheint das Test-Ereignis dort in Sekunden.
                  </Text>
                </Box>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="enabled"
            render={({ field }) => (
              <FormItem className="flex gap-4 mb-4 w-full grow">
                <FormLabel className="max-w-52 w-full font-bold mt-4 text-md">Tracking aktiv</FormLabel>
                <Box className="w-full mt-3">
                  <FormControl>
                    <Switch isChecked={field.value} onChange={(event) => field.onChange(event.target.checked)} />
                  </FormControl>
                  <FormMessage />
                </Box>
              </FormItem>
            )}
          />

          <Box className="mt-6 pt-6 border-t border-gray-200">
            <Text className="font-bold text-md mb-2">Verbindung prüfen</Text>
            <Text className="text-gray-400 text-sm mb-3">
              Schickt ein Test-Ereignis an die gespeicherte Pixel-ID – unabhängig davon, ob das Tracking aktiv ist.
              Danach im Events Manager unter „Test events“ nachsehen, ob es im richtigen Dataset gelandet ist. Bei
              generischen Dataset-Namen ist das die einzige Kontrolle gegen eine Verwechslung.
            </Text>
            <Button
              type="button"
              variant="outline"
              colorScheme="primary"
              className="flex gap-2 p-2 w-fit"
              isDisabled={!settings.configured || isSendingTest}
              isLoading={isSendingTest}
              onClick={sendTestEvent}
            >
              <Box boxSize={4} display="flex" alignItems="center" justifyContent="center">
                <Send size={16} />
              </Box>
              <span>Test-Event senden</span>
            </Button>
            {testResult && (
              <Text className={`text-sm mt-3 ${testResult.success ? "text-green-600" : "text-red-600"}`}>
                {testResult.success
                  ? "Meta hat das Test-Ereignis angenommen. Jetzt im Events Manager unter „Test events“ prüfen."
                  : `Meta hat das Test-Ereignis abgelehnt: ${testResult.errorMessage ?? "unbekannter Fehler"}`}
              </Text>
            )}
          </Box>
        </Flex>

        <Flex className="gap-4 justify-end mt-auto">
          <Button
            colorScheme="primary"
            color="white"
            className="flex gap-2 p-2 text-white ml-auto mt-4"
            variant="solid"
            type="submit"
          >
            <Box boxSize={5} display="flex" alignItems="center" justifyContent="center">
              <Save />
            </Box>
            <span>Speichern</span>
          </Button>
        </Flex>
      </form>
    </Form>
  );
};

export default ConversionTracking;
